import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Plus, Trash2, ChevronDown } from "lucide-react";
import { useHomeOSState } from "../state/HomeOSStateProvider";
import { AvatarUploader } from "./AvatarUploader";
import {
  RELATIONSHIPS, LIFE_STAGES, GOAL_CATEGORIES, FAVOURITE_CATEGORIES,
  RESPONSIBILITY_SUGGESTIONS, FOOD_FIELDS, CARE_FIELDS, LIVING_TEXT_FIELDS,
  CELEBRATION_LIST_FIELDS, blankIntelligence, migrateMemberToV2,
} from "../utils/memberIntelligence";

/* ════════════════════════════════════════════════════════════════════════
   FamilyMemberEditModal — the household record editor.
   Nine calm progressive-disclosure sections. Create once, remember once,
   use everywhere.
   ════════════════════════════════════════════════════════════════════════ */

const STONE = "#7A6E63";
const FOREST = "#24382C";
const inter = { fontFamily: "Inter, system-ui, sans-serif" };
const serif = { fontFamily: "var(--ara-font-display), 'Times New Roman', serif" };

const BLANK = {
  name: "", role: "",
  avatarType: "initial", photoUrl: null, color: STONE,
  ...blankIntelligence(),
};

const inputStyle = {
  ...inter, fontSize: 14, fontWeight: 400,
  color: "var(--ara-ink)", background: "var(--ara-surface)",
  border: "1px solid var(--ara-line)", borderRadius: 10,
  outline: "none", width: "100%", padding: "9px 12px",
};

function focusHandlers() {
  return {
    onFocus: (e) => { e.target.style.borderColor = STONE; e.target.style.boxShadow = `0 0 0 1px ${STONE}`; },
    onBlur: (e) => { e.target.style.borderColor = "var(--ara-line)"; e.target.style.boxShadow = "none"; },
  };
}

export function FamilyMemberEditModal({ open, onClose, memberId, mode = "edit" }) {
  const {
    members, upsertMember, addMember, removeMember, duplicateMember,
    goals, addGoal, removeGoal,
  } = useHomeOSState();
  const existing = memberId ? members.find(m => m.id === memberId) : null;

  const [form, setForm] = useState(BLANK);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [openSection, setOpenSection] = useState("about");

  useEffect(() => {
    if (!open) return;
    if (mode === "add" || !existing) {
      setForm({ ...BLANK, ...blankIntelligence() });
    } else {
      const v2 = migrateMemberToV2(existing);
      setForm({
        ...BLANK,
        ...v2,
        food: { ...blankIntelligence().food, ...(v2.food || {}) },
        care: { ...blankIntelligence().care, ...(v2.care || {}) },
        living: { ...blankIntelligence().living, ...(v2.living || {}) },
        celebrations: { ...blankIntelligence().celebrations, ...(v2.celebrations || {}) },
        relationships: v2.relationships || [],
        likesFavourites: v2.likesFavourites || [],
        responsibilities: v2.responsibilities || [],
        araShouldKnow: v2.araShouldKnow || [],
      });
    }
    setConfirmDelete(false);
    setOpenSection("about");
  }, [open, memberId, mode, existing]);

  const memberGoals = useMemo(
    () => (goals || []).filter(g => g.ownerType === "member" && g.ownerMemberId === memberId),
    [goals, memberId]
  );

  if (!open) return null;

  const set = (key, value) => setForm(s => ({ ...s, [key]: value }));
  const setNested = (group, key, value) =>
    setForm(s => ({ ...s, [group]: { ...s[group], [key]: value } }));

  const saveAll = () => {
    if (!form.name.trim()) {
      toast.error("Give them a name first.");
      return;
    }
    if (mode === "add" || !existing) {
      addMember({ ...form, intelligenceVersion: 2 });
      toast.success(`Added ${form.name} to your household.`);
    } else {
      upsertMember({ ...existing, ...form, intelligenceVersion: 2 });
      toast.success(`Saved changes to ${form.name}.`);
    }
    onClose();
  };

  const handleDelete = () => {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    removeMember(existing.id);
    toast(`${existing.name || "Member"} removed.`);
    onClose();
  };

  const handleDuplicate = () => {
    if (!existing) return;
    duplicateMember(existing.id);
    toast(`${existing.name} duplicated.`);
    onClose();
  };

  const countList = (arr) => (arr || []).filter(Boolean).length;
  const foodCount = FOOD_FIELDS.reduce((n, [k]) => n + countList(form.food?.[k]), 0);
  const careCount = CARE_FIELDS.reduce((n, [k]) => n + countList(form.care?.[k]), 0);
  const livingCount = LIVING_TEXT_FIELDS.reduce((n, [k]) => n + (String(form.living?.[k] || "").trim() ? 1 : 0), 0)
    + (form.living?.chronotype ? 1 : 0) + countList(form.living?.commitments);
  const celCount = CELEBRATION_LIST_FIELDS.reduce((n, [k]) => n + countList(form.celebrations?.[k]), 0)
    + (form.celebrations?.clothingSize ? 1 : 0) + (form.celebrations?.shoeSize ? 1 : 0);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="family-edit-modal">
      <div className="absolute inset-0" style={{ background: "rgba(31,31,31,0.35)" }} onClick={onClose} />

      <div
        className="relative w-full max-w-xl max-h-[92vh] overflow-y-auto rounded-t-[20px] sm:rounded-[20px] animate-fadeUp"
        style={{ background: "var(--ara-surface)", border: "1px solid var(--ara-line)" }}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 px-6 pt-6 pb-4" style={{ background: "var(--ara-surface)", borderBottom: "1px solid var(--ara-line-soft)" }}>
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="text-[11px] tracking-[0.16em] uppercase" style={{ ...inter, color: STONE, fontWeight: 500 }}>Family</p>
              <h2 className="mt-1 text-[17px] leading-tight" style={{ ...inter, color: "var(--ara-ink)", fontWeight: 500 }}>
                {mode === "add" || !existing ? "Add family member" : `Edit ${existing.name || "member"}`}
              </h2>
              <p className="mt-0.5 text-[12px]" style={{ ...inter, color: "var(--ara-ink-soft)", fontWeight: 400 }}>
                Everything here helps ARA look after this person, everywhere in the residence.
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 min-h-[36px] rounded-full flex items-center justify-center transition-opacity hover:opacity-70"
              style={{ border: "1px solid var(--ara-line)", color: "var(--ara-ink-soft)", background: "transparent" }}
              data-testid="family-edit-close"
              aria-label="Close"
            >
              <X className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {/* Body — nine calm sections */}
        <div className="px-6 py-2">

          <Disclosure id="about" label="About" count={null} openSection={openSection} setOpenSection={setOpenSection}>
            <AvatarUploader
              member={{ id: existing?.id || "preview", name: form.name, avatarType: form.avatarType, photoUrl: form.photoUrl, color: form.color }}
              onChange={(patch) => setForm(s => ({ ...s, ...patch }))}
            />
            <TextField label="Full name" value={form.name} onChange={(v) => set("name", v)} placeholder="e.g. Erana" testId="member-name" autoFocus />
            <TextField label="Preferred name / nickname" value={form.preferredName} onChange={(v) => set("preferredName", v)} placeholder="What everyone calls them" testId="member-preferred-name" />
            <MultiChipSelect
              label="Relationship to household"
              values={form.relationships}
              options={RELATIONSHIPS}
              onChange={(arr) => set("relationships", arr)}
              testId="member-relationship"
            />
            <ChipSelect label="Life stage" value={form.lifeStage} options={LIFE_STAGES} onChange={(v) => set("lifeStage", v)} testId="member-life-stage" allowClear />
            <TextField label="Birthday" value={form.birthday} onChange={(v) => set("birthday", v)} placeholder="e.g. 14 September" testId="member-birthday" hint="Kept once here — Planning reads it from this profile." />
            <TextField label="Pronouns (optional)" value={form.pronouns} onChange={(v) => set("pronouns", v)} placeholder="e.g. she/her" testId="member-pronouns" />
            <TextField label="Contact (optional)" value={form.contactInfo} onChange={(v) => set("contactInfo", v)} placeholder="Phone or email" testId="member-contact" />
          </Disclosure>

          <Disclosure id="food" label="Food & meals" count={foodCount} openSection={openSection} setOpenSection={setOpenSection}
            hint="The Kitchen and shopping lists will read from here.">
            {FOOD_FIELDS.map(([key, label]) => (
              <TagField key={key} label={label} items={form.food?.[key] || []}
                onChange={(arr) => setNested("food", key, arr)} testId={`member-food-${key}`} />
            ))}
          </Disclosure>

          <Disclosure id="favourites" label="Likes & favourites" count={countList(form.likesFavourites)} openSection={openSection} setOpenSection={setOpenSection}
            hint="Optional — ARA learns this person over time.">
            <FavouritesField
              entries={form.likesFavourites}
              onChange={(arr) => set("likesFavourites", arr)}
            />
          </Disclosure>

          <Disclosure id="care" label="Health & care" count={careCount} openSection={openSection} setOpenSection={setOpenSection}
            hint="High-level context only. Detailed health lives in Wellness.">
            {CARE_FIELDS.map(([key, label]) => (
              <TagField key={key} label={label} items={form.care?.[key] || []}
                onChange={(arr) => setNested("care", key, arr)} testId={`member-care-${key}`} />
            ))}
          </Disclosure>

          <Disclosure id="living" label="Routines & preferences" count={livingCount} openSection={openSection} setOpenSection={setOpenSection}
            hint="How this person lives. Scheduled routines stay in Planning.">
            <ChipSelect label="Morning or evening person" value={form.living?.chronotype}
              options={["Morning person", "Evening person", "Flexible"]}
              onChange={(v) => setNested("living", "chronotype", v)} testId="member-chronotype" allowClear />
            {LIVING_TEXT_FIELDS.map(([key, label, ph]) => (
              <TextField key={key} label={label} value={form.living?.[key]} placeholder={ph}
                onChange={(v) => setNested("living", key, v)} testId={`member-living-${key}`} />
            ))}
            <TagField label="Regular weekly commitments" items={form.living?.commitments || []}
              onChange={(arr) => setNested("living", "commitments", arr)} testId="member-living-commitments"
              placeholder="e.g. Netball Saturdays 9am" />
          </Disclosure>

          <Disclosure id="responsibilities" label="Responsibilities" count={countList(form.responsibilities)} openSection={openSection} setOpenSection={setOpenSection}
            hint="Ongoing household ownership. ARA will use this when assigning work.">
            <TagField label="Responsibilities" items={form.responsibilities}
              onChange={(arr) => set("responsibilities", arr)} testId="member-responsibilities"
              placeholder="Add responsibility" suggestions={RESPONSIBILITY_SUGGESTIONS} />
          </Disclosure>

          <Disclosure id="goals" label="Goals & dreams" count={memberGoals.length} openSection={openSection} setOpenSection={setOpenSection}
            hint="Kept in the household Goals — shown here and in the Goals view.">
            {(mode === "add" || !existing) ? (
              <p className="text-[13px] italic" style={{ ...serif, color: "var(--ara-ink-muted)" }}>
                Save this person first, then their goals can join the household Goals.
              </p>
            ) : (
              <GoalsEditor
                memberId={existing.id}
                memberGoals={memberGoals}
                addGoal={addGoal}
                removeGoal={removeGoal}
              />
            )}
          </Disclosure>

          <Disclosure id="celebrations" label="Celebrations & gift ideas" count={celCount} openSection={openSection} setOpenSection={setOpenSection}>
            <div className="flex items-baseline justify-between gap-6 pb-1">
              <FieldLabel>Birthday</FieldLabel>
              <span className="text-[13px]" style={{ ...inter, color: "var(--ara-ink)", fontWeight: 450 }} data-testid="celebrations-birthday-echo">
                {form.birthday?.trim() || "Add it in About"}
              </span>
            </div>
            {CELEBRATION_LIST_FIELDS.map(([key, label]) => (
              <TagField key={key} label={label} items={form.celebrations?.[key] || []}
                onChange={(arr) => setNested("celebrations", key, arr)} testId={`member-cel-${key}`}
                placeholder={key === "giftIdeas" ? "Add idea" : undefined} />
            ))}
            <div className="grid grid-cols-2 gap-4">
              <TextField label="Clothing size (optional)" value={form.celebrations?.clothingSize}
                onChange={(v) => setNested("celebrations", "clothingSize", v)} testId="member-clothing-size" />
              <TextField label="Shoe size (optional)" value={form.celebrations?.shoeSize}
                onChange={(v) => setNested("celebrations", "shoeSize", v)} testId="member-shoe-size" />
            </div>
          </Disclosure>

          <Disclosure id="ara" label="ARA should know" count={countList(form.araShouldKnow)} openSection={openSection} setOpenSection={setOpenSection}
            hint="Anything that helps ARA understand this person better.">
            <NoteListField
              items={form.araShouldKnow}
              onChange={(arr) => set("araShouldKnow", arr)}
              testId="member-ara-notes"
              placeholder={'e.g. "Needs quiet time after work."'}
            />
          </Disclosure>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 px-6 py-4 space-y-3" style={{ background: "var(--ara-surface)", borderTop: "1px solid var(--ara-line-soft)" }}>
          {confirmDelete && (
            <p className="text-[12.5px]" style={{ ...inter, color: "var(--ara-ink-muted)", fontWeight: 400 }} data-testid="member-delete-confirm">
              Remove {existing?.name || "this member"} from your household? This can't be undone.
            </p>
          )}

          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            {existing && mode !== "add" && (
              <>
                <button
                  onClick={handleDelete}
                  className="text-[12.5px] min-h-[44px] sm:min-h-0 transition-opacity hover:opacity-70"
                  style={{ ...inter, color: confirmDelete ? "#C77068" : "var(--ara-ink-soft)", fontWeight: confirmDelete ? 500 : 400, background: "none", border: "none", cursor: "pointer", padding: 0 }}
                  data-testid="member-delete-btn"
                >
                  {confirmDelete ? "Yes, remove" : "Remove member"}
                </button>
                <button
                  onClick={handleDuplicate}
                  className="text-[12.5px] min-h-[44px] sm:min-h-0 transition-opacity hover:opacity-70"
                  style={{ ...inter, color: "var(--ara-ink-soft)", fontWeight: 400, background: "none", border: "none", cursor: "pointer", padding: 0 }}
                  data-testid="member-duplicate-btn"
                >
                  Duplicate
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="ml-auto text-[13px] min-h-[44px] sm:min-h-0 transition-opacity hover:opacity-70"
              style={{ ...inter, color: "var(--ara-ink-muted)", fontWeight: 450, background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline", textUnderlineOffset: 3 }}
              data-testid="member-cancel-btn"
            >
              Cancel
            </button>
            <button
              onClick={saveAll}
              className="rounded-full px-5 py-2.5 min-h-[44px] text-[13px] transition-opacity hover:opacity-90"
              style={{ ...inter, background: FOREST, color: "#FAF9F6", fontWeight: 500, border: "none", cursor: "pointer" }}
              data-testid="member-save-btn"
            >
              {mode === "add" || !existing ? "Add member" : "Save changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Progressive-disclosure section ── */

function Disclosure({ id, label, count, hint, openSection, setOpenSection, children }) {
  const isOpen = openSection === id;
  return (
    <div style={{ borderBottom: "1px solid var(--ara-line-soft)" }} data-testid={`edit-section-${id}`}>
      <button
        type="button"
        onClick={() => setOpenSection(isOpen ? null : id)}
        className="w-full flex items-center justify-between gap-4 py-4 min-h-[48px] transition-opacity hover:opacity-75"
        style={{ background: "none", border: "none", cursor: "pointer", padding: "16px 0" }}
        data-testid={`edit-section-${id}-toggle`}
        aria-expanded={isOpen}
      >
        <span className="flex items-baseline gap-3">
          <span className="text-[11px] tracking-[0.16em] uppercase text-left" style={{ ...inter, color: STONE, fontWeight: 500 }}>
            {label}
          </span>
          {count !== null && count > 0 && (
            <span className="text-[11px]" style={{ ...inter, color: "var(--ara-ink-soft)", fontWeight: 400 }}>
              {count} recorded
            </span>
          )}
        </span>
        <ChevronDown
          className="w-4 h-4 flex-shrink-0 transition-transform"
          strokeWidth={1.5}
          style={{ color: "var(--ara-ink-soft)", transform: isOpen ? "rotate(180deg)" : "none" }}
        />
      </button>
      {isOpen && (
        <div className="pb-6 space-y-5">
          {hint && (
            <p className="text-[12px] italic" style={{ ...serif, color: "var(--ara-ink-muted)" }}>{hint}</p>
          )}
          {children}
        </div>
      )}
    </div>
  );
}

/* ── Residence form primitives ── */

function FieldLabel({ children }) {
  return (
    <span className="block text-[11px] tracking-[0.14em] uppercase" style={{ ...inter, color: "var(--ara-ink-soft)", fontWeight: 500 }}>
      {children}
    </span>
  );
}

function TextField({ label, value, onChange, placeholder, testId, autoFocus, hint }) {
  return (
    <label className="block">
      <FieldLabel>{label}</FieldLabel>
      <input
        type="text"
        autoFocus={autoFocus}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1.5"
        style={inputStyle}
        {...focusHandlers()}
        data-testid={testId}
      />
      {hint && <span className="block mt-1 text-[11.5px]" style={{ ...inter, color: "var(--ara-ink-soft)", fontWeight: 400 }}>{hint}</span>}
    </label>
  );
}

function ChipSelect({ label, value, options, onChange, testId, allowClear }) {
  return (
    <div data-testid={testId}>
      <FieldLabel>{label}</FieldLabel>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {options.map(opt => {
          const active = value === opt;
          return (
            <button
              key={opt}
              type="button"
              onClick={() => onChange(allowClear && active ? "" : opt)}
              className="px-3 py-1.5 min-h-[32px] rounded-full text-[12px] transition-opacity hover:opacity-80"
              style={{
                ...inter, fontWeight: active ? 500 : 400,
                background: active ? STONE : "transparent",
                color: active ? "#FAF9F6" : "var(--ara-ink-muted)",
                border: `1px solid ${active ? STONE : "var(--ara-line)"}`,
                cursor: "pointer",
              }}
              data-testid={`${testId}-${opt}`}
              aria-pressed={active}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MultiChipSelect({ label, values, options, onChange, testId }) {
  const toggle = (opt) => {
    const has = (values || []).includes(opt);
    onChange(has ? values.filter(v => v !== opt) : [...(values || []), opt]);
  };
  return (
    <div data-testid={testId}>
      <FieldLabel>{label}</FieldLabel>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {options.map(opt => {
          const active = (values || []).includes(opt);
          return (
            <button
              key={opt}
              type="button"
              onClick={() => toggle(opt)}
              className="px-3 py-1.5 min-h-[32px] rounded-full text-[12px] transition-opacity hover:opacity-80"
              style={{
                ...inter, fontWeight: active ? 500 : 400,
                background: active ? STONE : "transparent",
                color: active ? "#FAF9F6" : "var(--ara-ink-muted)",
                border: `1px solid ${active ? STONE : "var(--ara-line)"}`,
                cursor: "pointer",
              }}
              data-testid={`${testId}-${opt.replace(/[^a-zA-Z]+/g, "-")}`}
              aria-pressed={active}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* Compact chip list + add input. Optional one-tap suggestions. */
function TagField({ label, items, onChange, placeholder = "Add", testId, suggestions }) {
  const [draft, setDraft] = useState("");
  const add = (text) => {
    const t = String(text ?? draft).trim();
    if (!t || (items || []).includes(t)) { setDraft(""); return; }
    onChange([...(items || []), t]);
    setDraft("");
  };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const remainingSuggestions = (suggestions || []).filter(s => !(items || []).includes(s));
  return (
    <div data-testid={testId}>
      <FieldLabel>{label}</FieldLabel>
      {(items || []).length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {items.map((it, idx) => (
            <span
              key={`${it}-${idx}`}
              className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full text-[12px]"
              style={{ ...inter, color: "var(--ara-ink)", fontWeight: 400, border: "1px solid var(--ara-line)", background: "var(--ara-surface)" }}
              data-testid={`${testId}-item-${idx}`}
            >
              {it}
              <button
                type="button"
                onClick={() => remove(idx)}
                className="w-5 h-5 rounded-full flex items-center justify-center transition-opacity hover:opacity-60"
                style={{ color: "var(--ara-ink-soft)", background: "none", border: "none", cursor: "pointer" }}
                data-testid={`${testId}-remove-${idx}`}
                aria-label={`Remove ${it}`}
              >
                <X className="w-3 h-3" strokeWidth={1.5} />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="mt-2 flex items-center gap-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          style={{ ...inputStyle, padding: "7px 12px" }}
          {...focusHandlers()}
          data-testid={`${testId}-input`}
        />
        <button
          type="button"
          onClick={() => add()}
          className="inline-flex items-center gap-1 px-3 py-1.5 min-h-[32px] rounded-full text-[12px] flex-shrink-0 transition-opacity hover:opacity-70"
          style={{ ...inter, color: "var(--ara-ink-muted)", fontWeight: 450, background: "transparent", border: "1px solid var(--ara-line)", cursor: "pointer" }}
          data-testid={`${testId}-add`}
        >
          <Plus className="w-3 h-3" strokeWidth={1.5} />Add
        </button>
      </div>
      {remainingSuggestions.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {remainingSuggestions.map(s => (
            <button
              key={s}
              type="button"
              onClick={() => add(s)}
              className="px-2.5 py-1 rounded-full text-[11.5px] transition-opacity hover:opacity-70"
              style={{ ...inter, color: "var(--ara-ink-soft)", fontWeight: 400, background: "transparent", border: "1px dashed var(--ara-line)", cursor: "pointer" }}
              data-testid={`${testId}-suggestion-${s.replace(/[^a-zA-Z]+/g, "-")}`}
            >
              + {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* Free-form contextual notes, one per row. */
function NoteListField({ items, onChange, testId, placeholder }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const t = draft.trim();
    if (!t) return;
    onChange([...(items || []), t]);
    setDraft("");
  };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  return (
    <div data-testid={testId}>
      {(items || []).length > 0 && (
        <div className="space-y-2 mb-3">
          {items.map((it, idx) => (
            <div key={idx} className="flex items-start justify-between gap-3 py-1.5" style={{ borderBottom: "1px solid var(--ara-line-soft)" }} data-testid={`${testId}-item-${idx}`}>
              <p className="text-[13.5px] italic leading-[1.55]" style={{ ...serif, color: "var(--ara-ink)" }}>{it}</p>
              <button
                type="button"
                onClick={() => remove(idx)}
                className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-opacity hover:opacity-60"
                style={{ color: "var(--ara-ink-soft)", background: "none", border: "none", cursor: "pointer" }}
                data-testid={`${testId}-remove-${idx}`}
                aria-label="Remove note"
              >
                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          style={{ ...inputStyle, padding: "7px 12px" }}
          {...focusHandlers()}
          data-testid={`${testId}-input`}
        />
        <button
          type="button"
          onClick={add}
          className="inline-flex items-center gap-1 px-3 py-1.5 min-h-[32px] rounded-full text-[12px] flex-shrink-0 transition-opacity hover:opacity-70"
          style={{ ...inter, color: "var(--ara-ink-muted)", fontWeight: 450, background: "transparent", border: "1px solid var(--ara-line)", cursor: "pointer" }}
          data-testid={`${testId}-add`}
        >
          <Plus className="w-3 h-3" strokeWidth={1.5} />Add
        </button>
      </div>
    </div>
  );
}

/* Likes & favourites — category + value entries. */
function FavouritesField({ entries, onChange }) {
  const [category, setCategory] = useState(FAVOURITE_CATEGORIES[0]);
  const [draft, setDraft] = useState("");
  const add = () => {
    const t = draft.trim();
    if (!t) return;
    onChange([...(entries || []), { id: `fav-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 5)}`, category, value: t }]);
    setDraft("");
  };
  const remove = (id) => onChange(entries.filter(e => e.id !== id));
  return (
    <div data-testid="member-favourites">
      {(entries || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {entries.map((e, idx) => (
            <span
              key={e.id || idx}
              className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full text-[12px]"
              style={{ ...inter, color: "var(--ara-ink)", border: "1px solid var(--ara-line)" }}
              data-testid={`member-favourites-item-${idx}`}
            >
              <span style={{ color: "var(--ara-ink-soft)" }}>{e.category} ·</span> {e.value}
              <button
                type="button"
                onClick={() => remove(e.id)}
                className="w-5 h-5 rounded-full flex items-center justify-center transition-opacity hover:opacity-60"
                style={{ color: "var(--ara-ink-soft)", background: "none", border: "none", cursor: "pointer" }}
                data-testid={`member-favourites-remove-${idx}`}
                aria-label="Remove favourite"
              >
                <X className="w-3 h-3" strokeWidth={1.5} />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex flex-wrap gap-1.5 mb-2">
        {FAVOURITE_CATEGORIES.map(c => {
          const active = category === c;
          return (
            <button
              key={c}
              type="button"
              onClick={() => setCategory(c)}
              className="px-2.5 py-1 rounded-full text-[11.5px] transition-opacity hover:opacity-80"
              style={{
                ...inter, fontWeight: active ? 500 : 400,
                background: active ? STONE : "transparent",
                color: active ? "#FAF9F6" : "var(--ara-ink-soft)",
                border: `1px solid ${active ? STONE : "var(--ara-line)"}`, cursor: "pointer",
              }}
              data-testid={`member-favourites-cat-${c.replace(/[^a-zA-Z]+/g, "-")}`}
            >
              {c}
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={`Add a ${category.toLowerCase()}`}
          style={{ ...inputStyle, padding: "7px 12px" }}
          {...focusHandlers()}
          data-testid="member-favourites-input"
        />
        <button
          type="button"
          onClick={add}
          className="inline-flex items-center gap-1 px-3 py-1.5 min-h-[32px] rounded-full text-[12px] flex-shrink-0 transition-opacity hover:opacity-70"
          style={{ ...inter, color: "var(--ara-ink-muted)", fontWeight: 450, background: "transparent", border: "1px solid var(--ara-line)", cursor: "pointer" }}
          data-testid="member-favourites-add"
        >
          <Plus className="w-3 h-3" strokeWidth={1.5} />Add
        </button>
      </div>
    </div>
  );
}

/* Goals — writes straight to the central household goals. */
function GoalsEditor({ memberId, memberGoals, addGoal, removeGoal }) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ title: "", category: "Personal", target: "", targetDate: "", why: "" });

  const submit = () => {
    if (!draft.title.trim()) { toast.error("Give the goal a title."); return; }
    addGoal({
      ...draft,
      title: draft.title.trim(),
      ownerType: "member",
      ownerMemberId: memberId,
      status: "in_progress",
    });
    setDraft({ title: "", category: "Personal", target: "", targetDate: "", why: "" });
    setAdding(false);
    toast.success("Goal added to the household Goals.");
  };

  return (
    <div data-testid="member-goals">
      {memberGoals.length > 0 && (
        <div className="space-y-0 mb-4">
          {memberGoals.map((g, idx) => (
            <div key={g.id} className="flex items-start justify-between gap-3 py-2.5" style={{ borderBottom: "1px solid var(--ara-line-soft)" }} data-testid={`member-goal-${idx}`}>
              <div className="min-w-0">
                <p className="text-[14px]" style={{ ...inter, color: "var(--ara-ink)", fontWeight: 450 }}>{g.title}</p>
                <p className="mt-0.5 text-[12px]" style={{ ...inter, color: "var(--ara-ink-soft)" }}>
                  {[g.category, g.target, g.targetDate].filter(Boolean).join(" · ")}
                </p>
                {g.why && <p className="mt-0.5 text-[12px] italic" style={{ ...serif, color: "var(--ara-ink-muted)" }}>{g.why}</p>}
              </div>
              <button
                type="button"
                onClick={() => { removeGoal(g.id); toast(`Goal removed.`); }}
                className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-opacity hover:opacity-60"
                style={{ color: "var(--ara-ink-soft)", background: "none", border: "none", cursor: "pointer" }}
                data-testid={`member-goal-remove-${idx}`}
                aria-label="Remove goal"
              >
                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
              </button>
            </div>
          ))}
        </div>
      )}

      {!adding ? (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-1 px-3 py-1.5 min-h-[36px] rounded-full text-[12.5px] transition-opacity hover:opacity-70"
          style={{ ...inter, color: "var(--ara-ink-muted)", fontWeight: 450, background: "transparent", border: "1px solid var(--ara-line)", cursor: "pointer" }}
          data-testid="member-goal-add-btn"
        >
          <Plus className="w-3 h-3" strokeWidth={1.5} />Add goal
        </button>
      ) : (
        <div className="space-y-4">
          <TextField label="Goal" value={draft.title} onChange={(v) => setDraft(d => ({ ...d, title: v }))} placeholder="e.g. Learn to swim 50m" testId="member-goal-title" autoFocus />
          <ChipSelect label="Category" value={draft.category} options={GOAL_CATEGORIES} onChange={(v) => setDraft(d => ({ ...d, category: v }))} testId="member-goal-category" />
          <div className="grid grid-cols-2 gap-4">
            <TextField label="Target (optional)" value={draft.target} onChange={(v) => setDraft(d => ({ ...d, target: v }))} placeholder="e.g. 50 metres" testId="member-goal-target" />
            <TextField label="Target date (optional)" value={draft.targetDate} onChange={(v) => setDraft(d => ({ ...d, targetDate: v }))} placeholder="e.g. December" testId="member-goal-date" />
          </div>
          <TextField label="Why this matters (optional)" value={draft.why} onChange={(v) => setDraft(d => ({ ...d, why: v }))} testId="member-goal-why" />
          <div className="flex items-center gap-5">
            <button
              type="button"
              onClick={() => setAdding(false)}
              className="text-[12.5px] transition-opacity hover:opacity-70"
              style={{ ...inter, color: "var(--ara-ink-muted)", background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline", textUnderlineOffset: 3 }}
              data-testid="member-goal-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              className="rounded-full px-4 py-2 min-h-[36px] text-[12.5px] transition-opacity hover:opacity-90"
              style={{ ...inter, background: FOREST, color: "#FAF9F6", fontWeight: 500, border: "none", cursor: "pointer" }}
              data-testid="member-goal-save"
            >
              Save goal
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
